import {UseQueryOptions} from '@tanstack/react-query';
import {useEffect, useRef, useState} from 'react';
import {PagewebError} from '../errors/PagewebError.class';
import {CodegenLazyReactQueryType} from './CodegenLazyReactQueryType.type';
import {ReactQueryGetKeyVariablesRequired} from './CodegenReactQueryType.type';

export function useLazyCodegenReactQuery<
  TQuery,
  TValues,
  TError = PagewebError,
>(
  codegenReactQuery: CodegenLazyReactQueryType<TQuery, TValues, TError>,
  reactQueryOptions?: {
    variables?: TValues;
    options?: Omit<UseQueryOptions<TQuery, TError>, 'queryKey'>;
  }
): {
  fetchWithVariables: (
    variables?: TValues
  ) => Promise<ReturnType<CodegenLazyReactQueryType<TQuery, TValues, TError>>>;
} {
  return useSomeQuery<
    CodegenLazyReactQueryType<TQuery, TValues, TError>,
    TValues
  >(codegenReactQuery, reactQueryOptions);
}

export function useLazyReactQuery<
  UseQueryHook extends ((...args: any) => any) &
    ReactQueryGetKeyVariablesRequired<Parameters<UseQueryHook>[0]>,
  TVariables extends Parameters<UseQueryHook>[0],
  TOptions extends Omit<Parameters<UseQueryHook>[1], 'queryKey'>,
>(
  useReactQuery: UseQueryHook,
  reactQueryOptions?: {
    variables?: TVariables;
    options?: TOptions;
  }
): {
  fetchWithVariables: (
    variables: TVariables
  ) => Promise<ReturnType<UseQueryHook>>;
} {
  return useSomeQuery<UseQueryHook, TVariables>(
    useReactQuery,
    reactQueryOptions
  );
}

function useSomeQuery<UseQueryHook extends (...args: any) => any, TVariables>(
  useReactQuery: any,
  reactQueryOptions: any
): any {
  const [variables, setVariables] = useState<TVariables | undefined>(
    reactQueryOptions?.variables
  );
  const [refetchProcessId, setRefetchProcessId] = useState<symbol | undefined>(
    undefined
  );
  const {refetch} = useReactQuery(variables, {
    ...reactQueryOptions?.options,
    enabled: false,
  });
  const promiseRef = useRef<{
    [key: symbol]: {
      resolver: (value: ReturnType<UseQueryHook>) => void | undefined;
      reject: (error: Error) => void | undefined;
    };
  }>({});

  const fetchWithVariables = async (
    variables: TVariables
  ): Promise<ReturnType<UseQueryHook>> => {
    setVariables(variables);
    const newRefetchProcessId = Symbol();
    setRefetchProcessId(newRefetchProcessId);
    return new Promise<ReturnType<UseQueryHook>>((resolve, reject) => {
      promiseRef.current[newRefetchProcessId] = {resolver: resolve, reject};
    });
  };
  useEffect(() => {
    if (refetchProcessId) {
      refetch({throwOnError: true})
        .then((result: ReturnType<UseQueryHook>) => {
          promiseRef.current[refetchProcessId]?.resolver?.(result);
          delete promiseRef.current[refetchProcessId];
        })
        .catch((error: Error) => {
          promiseRef.current[refetchProcessId]?.reject?.(error);
          delete promiseRef.current[refetchProcessId];
        });
    }
  }, [refetchProcessId, refetch]);
  return {
    fetchWithVariables,
  };
}
